import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import { setPricing, saveOnboardingState } from '../../redux/onboardingReducer';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';

const CheckIcon = ({ className, isDarkMode }) => (
  <svg className={`w-5 h-5 ${className} ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const useIsDarkMode = () => {
  const theme = useTheme();
  const [isDarkMode, setIsDarkMode] = useState(theme.palette.mode === "dark");

  useEffect(() => {
    setIsDarkMode(theme.palette.mode === "dark");
  }, [theme.palette.mode]);

  return isDarkMode;
};

const PricingCard = ({ title, price, minutes, features, onSelect, isPremium, index, isSelected, isCurrentPlan }) => {
  const isDarkMode = useIsDarkMode();

  return (
    <motion.div 
      className={`${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-b from-gray-50 to-gray-100'} rounded-lg shadow-2xl cursor-pointer ${
        isPremium ? `border-4 ${isDarkMode ? 'border-yellow-300' : 'border-yellow-500'}` : ''
      } ${isSelected ? 'ring-2 ring-blue-500' : ''} ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}
      onClick={() => onSelect(title.toLowerCase(), price)}
      initial={{ opacity: 0, y: isPremium ? 100 : 20, scale: isPremium ? 1.05 : 1 }}
      animate={{ 
        opacity: 1, 
        y: 0, 
        scale: isPremium ? 1.05 : 1,
        boxShadow: isPremium ? '0 25px 50px -12px rgba(234, 179, 8, 0.25)' : 'none'
      }}
      transition={{ 
        type: "spring",
        stiffness: 100,
        damping: 20,
        delay: index * 0.1
      }}
      whileHover={{ 
        scale: isPremium ? 1.08 : 1.03,
        boxShadow: isPremium 
          ? '0 35px 60px -15px rgba(234, 179, 8, 0.3)' 
          : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        transition: { 
          type: "spring", 
          stiffness: 300, 
          damping: 20 
        }
      }}
    >
      <motion.div 
        className={`p-8 ${isPremium ? isDarkMode ? 'bg-gradient-to-br from-yellow-900 via-yellow-800 to-yellow-700' : 'bg-gradient-to-br from-yellow-100 via-yellow-200 to-yellow-300' : ''}`}
        whileHover={isPremium ? { 
          background: isDarkMode ? 'linear-gradient(135deg, #78350f 0%, #92400e 50%, #b45309 100%)' : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 50%, #fcd34d 100%)'
        } : {}}
      >
        <h2 className={`${isPremium ? 'text-3xl' : 'text-2xl'} font-extrabold mb-2 text-center ${isPremium ? isDarkMode ? 'text-yellow-100' : 'text-yellow-900' : isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          {title}
        </h2>
        <div className="text-center">
          <p className={`${isPremium ? 'text-2xl' : 'text-3xl'} font-black mb-2 ${isPremium ? isDarkMode ? 'text-yellow-200' : 'text-yellow-800' : isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            ₹{price}/{isPremium ? (
              <span className="text-3xl">{minutes}</span>
            ) : (
              <span className="text-2xl">{minutes}</span>
            )}
            <span className={`${isPremium ? 'text-2xl' : 'text-lg'} font-bold ml-1 ${isPremium ? isDarkMode ? 'text-yellow-100' : 'text-yellow-700' : ''}`}>minutes</span>
          </p>
          <p className={`text-sm font-bold mb-3 ${isPremium ? isDarkMode ? 'text-yellow-300' : 'text-yellow-600' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            (excluding GST)
          </p>
        </div>
        <ul className="space-y-3 text-base">
          {features.map((feature, index) => (
            <motion.li key={index} className="flex items-center" whileHover={{ x: 5 }}>
              <CheckIcon 
                className={`mr-3 ${
                  isPremium 
                    ? isDarkMode ? 'text-yellow-300' : 'text-yellow-600'
                    : isDarkMode ? 'text-green-400' : 'text-green-500'
                }`} 
              />
              <span className={`${
                isPremium 
                  ? isDarkMode ? 'text-yellow-100 font-semibold' : 'text-yellow-900 font-semibold'
                  : isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {feature}
              </span>
            </motion.li>
          ))}
        </ul>
      </motion.div>
      <div className={`p-6 ${isPremium ? isDarkMode ? 'bg-gradient-to-r from-yellow-600 to-yellow-500' : 'bg-gradient-to-r from-yellow-400 to-yellow-500' : isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
        <motion.button 
          className={`w-full py-4 px-4 rounded-full font-extrabold ${isPremium ? 'text-xl' : 'text-lg'} ${
            isCurrentPlan
              ? 'bg-green-600 text-white'
              : isSelected
                ? isPremium
                  ? isDarkMode ? 'bg-yellow-500 text-gray-900 hover:bg-yellow-400' : 'bg-yellow-700 text-white hover:bg-yellow-600'
                  : isDarkMode ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-700 text-white hover:bg-blue-600'
                : isPremium 
                  ? isDarkMode ? 'bg-yellow-600 text-gray-900 hover:bg-yellow-500' : 'bg-yellow-500 text-gray-900 hover:bg-yellow-400' 
                  : isDarkMode ? 'bg-blue-500 text-white hover:bg-blue-400' : 'bg-blue-600 text-white hover:bg-blue-500'
          }`}
          onClick={() => onSelect(title.toLowerCase(), price)}
          whileHover={{ scale: 1.05, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          {isCurrentPlan 
            ? 'Current Plan' 
            : isSelected
              ? isPremium
                ? 'Selected Premium Plan'
                : 'Selected'
              : isPremium 
                ? 'Select Premium Plan' 
                : 'Select Plan'}
        </motion.button>
      </div>
    </motion.div>
  );
};

const Plans = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const savedPlan = useSelector(state => state.onboarding.pricing.plan);
  const [userId, setUserId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSavedPlan, setLastSavedPlan] = useState(null);
  const isDarkMode = useIsDarkMode();

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    if (storedUserId) {
      setUserId(storedUserId);
    } else {
      toast.error('User not found. Please log in again.');
      navigate('/login');
    }

    if (savedPlan) {
      setSelectedPlan(savedPlan);
      setLastSavedPlan(savedPlan);
    }
    // Enable smooth scrolling
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, [savedPlan]);

  const handleSelectPlan = (plan, price) => {
    setSelectedPlan(plan);
  };

  const handleConfirmPlan = async () => {
    if (selectedPlan && selectedPlan !== savedPlan) {
      if (!userId) {
        toast.error('User ID is missing. Please ensure you are logged in.');
        return;
      }

      setIsLoading(true);

      try {
        const selectedPlanDetails = plans.find(p => p.title.toLowerCase() === selectedPlan);
        const requestData = {
          userId: userId,
          planDetails: {
            name: selectedPlan,
            price: selectedPlanDetails.price,
            minutes:selectedPlanDetails.minutes
          }
        };

        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/plan`, requestData);

        dispatch(setPricing({ plan: selectedPlan, price: selectedPlanDetails.price }));
        dispatch(saveOnboardingState());

        setLastSavedPlan(selectedPlan);
        toast.success('Plan updated successfully');
      } catch (error) {
        toast.error('Failed to update plan. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const features = [
    'Multilingual Call Handling Agent',
    'Self-Learning and Continuous Improvement',
    'Voice Customization',
    'Personality Customization',
    'Multiple Use Case Options',
    'Advance Actionable Agent',
    'Automated Appointment Scheduling',
    'Advanced Call Routing',
    'Sentiment Analysis',
    'Proactive Engagement',
    'Advanced AI Voice Support',
    'High Voice Recognition Accuracy',
    'Advanced Analytics and Reporting',
    'Comprehensive Logs and Monitoring',
    'Conversation Transcript',
    'Conversation Audio Recordings',
    'Email and Call Support',
    'Secure and Compliant'
  ];

  const plans = [
    {
      title: 'Basic',
      price: 4999,
      minutes: 1500,
      features: features
    },
    {
      title: 'Standard',
      price: 14999,
      minutes: 6000,
      features: features
    },
    {
      title: 'Premium',
      price: 29999,
      minutes: 12000,
      features: features,
      isPremium: true
    }
  ];

  return (
    <div className="w-full flex flex-col justify-between items-center px-4 sm:px-6 lg:px-8 font-sans">
      <div className="w-full max-w-7xl flex-grow flex flex-col justify-center items-center pb-36">
      <motion.h1 
          className={`text-5xl font-black text-center mb-4 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, type: 'spring', stiffness: 100 }}
        >
          Pick a Plan
        </motion.h1>
        <motion.p 
          className={`text-2xl text-center mb-16 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} font-semibold`}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2, type: 'spring', stiffness: 100 }}
        >
          Choose the perfect plan for your business needs
        </motion.p>
          <div className="w-full grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
            {plans.map((plan, index) => (
              <div key={plan.title} className="flex justify-center">
                <PricingCard
                  {...plan}
                  onSelect={handleSelectPlan}
                  index={index}
                  isSelected={selectedPlan === plan.title.toLowerCase()}
                  isCurrentPlan={savedPlan === plan.title.toLowerCase()}
                />
              </div>
            ))}
        </div>
      </div>
      {selectedPlan && (
        <motion.div 
          className={`fixed bottom-0 left-0 right-0 ${isDarkMode ? 'bg-gray-800 bg-opacity-90' : 'bg-white bg-opacity-90'} shadow-lg p-4`}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div>
              <p className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                Selected: {selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} plan
              </p>
              <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                ₹{plans.find(p => p.title.toLowerCase() === selectedPlan)?.price.toLocaleString()} / {plans.find(p => p.title.toLowerCase() === selectedPlan)?.minutes.toLocaleString()} mins
              </p>
            </div>
            <button
              className={`px-8 py-3 text-lg font-semibold text-white rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-all duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-xl ${
                lastSavedPlan === selectedPlan
                  ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              }`}
              onClick={handleConfirmPlan}
              disabled={isLoading || lastSavedPlan === selectedPlan}
            >
              {isLoading 
                ? 'Processing...' 
                : lastSavedPlan === selectedPlan 
                  ? 'Plan Saved' 
                  : 'Confirm Plan Change'}
            </button>
          </div>
        </motion.div>
      )}
      <Toaster position="top-right" className='mt-24' expand={false} richColors />
    </div>
  );
};

export default Plans;
